import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { agentSkillContract } from "@common/contracts/agent-skill.contract";
import {
    type AgentSkillChatEditBody,
    type AgentSkillCreateBody,
    type AgentSkillListQueryInput,
    type AgentSkillUpdateBody,
    agentSkillRevisionListResponseSchema,
} from "@common/schemas/domains/agentSkills/agent-skill.schema";
import { getBackendAPIClient } from "@lib/backendAPIClient";
import { SUPPRESS_ERROR_TOAST_HEADER } from "@lib/backendClient";
import { useScope } from "@state/scopeContext.lib";

const agentSkillClient = getBackendAPIClient(agentSkillContract);

export const agentSkillQueryKeys = {
    all: (organizationId: string) => ["agent-skills", organizationId] as const,
    list: (organizationId: string, query: AgentSkillListQueryInput) =>
        ["agent-skills", organizationId, "list", query] as const,
    detail: (organizationId: string, skillId: string) =>
        ["agent-skills", organizationId, skillId] as const,
    revisions: (organizationId: string, skillId: string) =>
        ["agent-skills", organizationId, skillId, "revisions"] as const,
};

const suppressToast = { [SUPPRESS_ERROR_TOAST_HEADER]: "true" };

export function useAgentSkills(query: AgentSkillListQueryInput) {
    const { organizationId } = useScope();

    return useQuery({
        queryKey: organizationId
            ? agentSkillQueryKeys.list(organizationId, query)
            : [],
        queryFn: async () => {
            if (!organizationId) {
                return [];
            }
            const response = await agentSkillClient.listAgentSkills({
                params: { organizationId },
                query,
            });
            return response.body.skills;
        },
        enabled: !!organizationId,
    });
}

export function useAgentSkill(skillId: string | null) {
    const { organizationId } = useScope();

    return useQuery({
        queryKey:
            organizationId && skillId
                ? agentSkillQueryKeys.detail(organizationId, skillId)
                : [],
        queryFn: async () => {
            if (!organizationId || !skillId) {
                return null;
            }
            const response = await agentSkillClient.getAgentSkill({
                params: { organizationId, skillId },
            });
            return response.body;
        },
        enabled: !!organizationId && !!skillId,
    });
}

export function useAgentSkillRevisions(
    skillId: string | null,
    options?: { enabled?: boolean },
) {
    const { organizationId } = useScope();

    return useQuery({
        queryKey:
            organizationId && skillId
                ? agentSkillQueryKeys.revisions(organizationId, skillId)
                : [],
        queryFn: async () => {
            if (!organizationId || !skillId) {
                return [];
            }
            const response = await agentSkillClient.listAgentSkillRevisions({
                params: { organizationId, skillId },
            });
            return agentSkillRevisionListResponseSchema.parse(response.body)
                .revisions;
        },
        enabled: !!organizationId && !!skillId && (options?.enabled ?? true),
    });
}

const useInvalidateAgentSkills = () => {
    const queryClient = useQueryClient();
    const { organizationId } = useScope();

    return async () => {
        if (!organizationId) {
            return;
        }
        await queryClient.invalidateQueries({
            queryKey: agentSkillQueryKeys.all(organizationId),
        });
    };
};

const useRequireOrganizationId = () => {
    const { organizationId } = useScope();
    return () => {
        if (!organizationId) {
            throw new Error("No active organization");
        }
        return organizationId;
    };
};

export function useCreateAgentSkillMutation() {
    const requireOrganizationId = useRequireOrganizationId();
    const invalidate = useInvalidateAgentSkills();

    return useMutation({
        mutationFn: async (body: AgentSkillCreateBody) => {
            const response = await agentSkillClient.createAgentSkill({
                params: { organizationId: requireOrganizationId() },
                body,
                extraHeaders: suppressToast,
            });
            return response.body;
        },
        onSuccess: invalidate,
    });
}

export function useUpdateAgentSkillMutation() {
    const requireOrganizationId = useRequireOrganizationId();
    const invalidate = useInvalidateAgentSkills();

    return useMutation({
        mutationFn: async ({
            skillId,
            body,
        }: {
            skillId: string;
            body: AgentSkillUpdateBody;
        }) => {
            const response = await agentSkillClient.updateAgentSkill({
                params: { organizationId: requireOrganizationId(), skillId },
                body,
                extraHeaders: suppressToast,
            });
            return response.body;
        },
        onSuccess: invalidate,
    });
}

export function useChatEditAgentSkillMutation() {
    const requireOrganizationId = useRequireOrganizationId();
    const invalidate = useInvalidateAgentSkills();

    return useMutation({
        mutationFn: async ({
            skillId,
            body,
        }: {
            skillId: string;
            body: AgentSkillChatEditBody;
        }) => {
            const response = await agentSkillClient.chatEditAgentSkill({
                params: { organizationId: requireOrganizationId(), skillId },
                body,
                extraHeaders: suppressToast,
            });
            return response.body;
        },
        onSuccess: invalidate,
    });
}

type SkillIdRequest = (params: {
    organizationId: string;
    skillId: string;
}) => Promise<{ status: number; body: object }>;

const useSkillIdMutation = (request: SkillIdRequest) => {
    const requireOrganizationId = useRequireOrganizationId();
    const invalidate = useInvalidateAgentSkills();

    return useMutation({
        mutationFn: async (skillId: string) => {
            const response = await request({
                organizationId: requireOrganizationId(),
                skillId,
            });
            return response.body;
        },
        onSuccess: invalidate,
    });
};

export const useEnableAgentSkillMutation = () =>
    useSkillIdMutation((params) =>
        agentSkillClient.enableAgentSkill({
            params,
            extraHeaders: suppressToast,
        }),
    );

export const useDisableAgentSkillMutation = () =>
    useSkillIdMutation((params) =>
        agentSkillClient.disableAgentSkill({
            params,
            extraHeaders: suppressToast,
        }),
    );

export const useArchiveAgentSkillMutation = () =>
    useSkillIdMutation((params) =>
        agentSkillClient.archiveAgentSkill({
            params,
            extraHeaders: suppressToast,
        }),
    );

export function useResetAgentSkillMutation() {
    const requireOrganizationId = useRequireOrganizationId();
    const invalidate = useInvalidateAgentSkills();

    return useMutation({
        mutationFn: async (skillId: string) => {
            const response = await agentSkillClient.resetAgentSkill({
                params: { organizationId: requireOrganizationId(), skillId },
                extraHeaders: suppressToast,
            });
            return response.body;
        },
        onSuccess: invalidate,
    });
}

export function useRestoreAgentSkillRevisionMutation() {
    const requireOrganizationId = useRequireOrganizationId();
    const invalidate = useInvalidateAgentSkills();

    return useMutation({
        mutationFn: async ({
            skillId,
            revisionId,
        }: {
            skillId: string;
            revisionId: string;
        }) => {
            const response = await agentSkillClient.restoreAgentSkillRevision({
                params: {
                    organizationId: requireOrganizationId(),
                    skillId,
                    revisionId,
                },
                extraHeaders: suppressToast,
            });
            return response.body;
        },
        onSuccess: invalidate,
    });
}
